/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { handleResponse } from '@opengeoweb/api';
import { loadPlugin } from '@opengeoweb/plugin-interface';
import { sessionStorageProvider } from '@opengeoweb/shared';
import { resolveLiveReloadPluginUrl } from './livereload';
import { getHighestVersion, sortVersionsDescending } from '../utils/semver';

interface Plugin {
  type: 'view' | 'overlay';
  scopes: string[];
  distArtifact?: string;
  bundle?: string;
}

interface Package {
  name: string;
  version: string;
  description: string;
  author: { name: string };
  license: string;
  geoweb: { plugin: Plugin };
}

interface PackageMetadata {
  _id: string;
  versions: Record<string, Package>;
  time?: { created: string; modified: string };
  'dist-tags'?: Record<string, string>;
}

export interface PluginMeta {
  id: string;
  name: string;
  url: string;
  author?: { name?: string };
  version?: string;
  versions?: string[];
  type?: 'view' | 'overlay';
  description?: string;
  license?: string;
  created?: string;
  modified?: string;
}

export type PluginRegistry = Record<string, PluginMeta>;
export const TEST_PLUGIN_ID = 'test-plugin';
let isTestPluginOffline = false;

const getPluginBaseUrl = (): string => {
  const config = sessionStorageProvider.getConfig();
  return config?.GW_PLUGIN_BACKEND_URL || '';
};

const getPluginTestUrl = (): string | undefined => {
  const config = sessionStorageProvider.getConfig();
  return config?.GW_PLUGIN_TEST_URL || undefined;
};

export const getBundleUrl = (packageName: string, version: string): string => {
  const baseUrl = getPluginBaseUrl();
  return `${baseUrl}/npm/geoweb/plugins/${packageName}/${version}/bundle`;
};

/**
 * The versions of a package that declare a GeoWeb plugin. A package can gain
 * or lose its `geoweb.plugin` block over its lifetime, so this is checked per
 * version instead of only on the version that ends up loaded.
 */
const getPluginVersions = (manifest: PackageMetadata): string[] =>
  Object.keys(manifest.versions ?? {}).filter(
    (version) => !!manifest.versions[version]?.geoweb?.plugin,
  );

/**
 * The version loaded by default: the `latest` dist-tag when it names a plugin
 * version, otherwise the highest version by semver precedence. Registry key
 * order is never used, as it reflects publish order rather than the versions.
 */
const resolveDefaultVersion = (
  manifest: PackageMetadata,
  versions: string[],
): string | undefined => {
  const latest = manifest['dist-tags']?.latest;

  return latest && versions.includes(latest)
    ? latest
    : getHighestVersion(versions);
};

const createPluginMeta = (
  packageName: string,
  manifest: PackageMetadata,
): PluginMeta | undefined => {
  const pluginVersions = getPluginVersions(manifest);
  const defaultVersion = resolveDefaultVersion(manifest, pluginVersions);
  const defaultPackage = defaultVersion
    ? manifest.versions[defaultVersion]
    : undefined;

  if (!defaultPackage) {
    return undefined;
  }

  return {
    id: manifest._id,
    name: defaultPackage.name,
    version: defaultPackage.version,
    description: defaultPackage.description,
    author: defaultPackage.author,
    license: defaultPackage.license,
    type: defaultPackage.geoweb.plugin.type,
    created: manifest.time?.created,
    modified: manifest.time?.modified,
    url: getBundleUrl(packageName, defaultPackage.version),
    versions: sortVersionsDescending(pluginVersions),
  };
};

export const loadRegistryPlugins = async (): Promise<PluginRegistry> => {
  const baseUrl = getPluginBaseUrl();

  if (!baseUrl) {
    return {};
  }

  const response = await fetch(`${baseUrl}/npm/geoweb/plugins/metadata`);
  const data: {
    manifests: { packageName: string; manifest: PackageMetadata }[];
  } = await handleResponse(response);

  const registry: PluginRegistry = {};

  for (const { packageName, manifest } of data.manifests) {
    try {
      const pluginMeta = createPluginMeta(packageName, manifest);

      if (pluginMeta) {
        registry[manifest._id] = pluginMeta;
      }
    } catch (e) {
      console.warn(`Failed to process plugin ${manifest._id}:`, e);
    }
  }

  return registry;
};

export const loadLocalTestPlugin = async (): Promise<PluginMeta | null> => {
  const testUrl = getPluginTestUrl();

  if (!testUrl || isTestPluginOffline) {
    return null;
  }

  try {
    let packageMetadata: Package | null = null;
    let pluginUrl = testUrl;

    try {
      const packageJsonUrl = `${testUrl}/package.json`;
      const packageResponse = await fetch(packageJsonUrl);

      if (packageResponse.ok) {
        packageMetadata = await packageResponse.json();

        const bundlePath =
          packageMetadata?.geoweb?.plugin?.bundle ??
          packageMetadata?.geoweb?.plugin?.distArtifact;

        if (bundlePath) {
          const normalizedBundlePath = bundlePath.replace(/^dist\//, '');
          pluginUrl = `${testUrl}/${normalizedBundlePath}`;
        }
      }
    } catch (packageError) {
      if ((packageError as Error)?.message?.includes('Failed to fetch')) {
        console.warn(
          `GW_PLUGIN_TEST_URL is configured (${testUrl}) but the dev server is not reachable. ` +
            'Skipping local test plugin until the page is reloaded. Start the dev server to enable live reload.',
        );
        isTestPluginOffline = true;
        return null;
      }

      console.warn('Could not load package.json metadata:', packageError);
    }

    pluginUrl = await resolveLiveReloadPluginUrl(pluginUrl);
    const plugin = await loadPlugin(pluginUrl);

    return {
      id: TEST_PLUGIN_ID,
      name: packageMetadata?.name || plugin.name || 'Test Plugin',
      version: packageMetadata?.version || 'dev',
      description:
        packageMetadata?.description ||
        plugin.description ||
        'Plugin loaded from test URL for development',
      author: packageMetadata?.author,
      license: packageMetadata?.license,
      type: packageMetadata?.geoweb?.plugin?.type || plugin.type || 'view',
      url: pluginUrl,
    };
  } catch (e) {
    const message = (e as Error)?.message ?? String(e);

    if (message.includes('Failed to fetch dynamically imported module')) {
      console.warn(
        `GW_PLUGIN_TEST_URL is configured (${testUrl}) but no plugin bundle could be loaded from the dev server. ` +
          'Is your local plugin dev server running and serving the plugin bundle?',
      );
      isTestPluginOffline = true;
      return null;
    }

    console.warn(`Failed to process test plugin from URL ${testUrl}:`, e);
    return null;
  }
};

export const getPluginCatalog = async (): Promise<PluginRegistry> => {
  let registry: PluginRegistry = {};

  try {
    registry = await loadRegistryPlugins();
  } catch (e) {
    console.warn('Failed to load plugin registry:', e);
  }

  const testPlugin = await loadLocalTestPlugin();

  if (testPlugin) {
    registry[testPlugin.id] = testPlugin;
  }

  return registry;
};
